import { useState } from 'react';
import { Shield, Globe, Lock, ChevronRight, AlertCircle, Check, Building2, Activity, Ban, Clock, BarChart2, List, Settings as SettingsIcon, Download, Trash2, AlertTriangle, Play, ChevronDown, Eye } from 'lucide-react';

// Mock data for screenshots - no personal data
const MOCK_TODAY = {
  apsScore: 62,
  totals: { companies: 18, domains: 34, requests: 247 },
  topCategories: [
    { category: 'ADVERTISING', label: 'Advertising', count: 89, color: '#EF4444' },
    { category: 'ANALYTICS', label: 'Analytics', count: 71, color: '#3B82F6' },
    { category: 'SOCIAL', label: 'Social Media', count: 43, color: '#8B5CF6' },
    { category: 'SESSION_REPLAY', label: 'Session Replay', count: 28, color: '#F59E0B' },
    { category: 'CDN', label: 'CDN / Hosting', count: 16, color: '#6B7280' },
  ],
  topCompanies: [
    { name: 'Google', categories: ['ADVERTISING'], count: 67, companyId: 'google' },
    { name: 'Meta', categories: ['SOCIAL'], count: 43, companyId: 'meta' },
    { name: 'Amazon', categories: ['ADVERTISING'], count: 31, companyId: 'amazon' },
    { name: 'Hotjar', categories: ['SESSION_REPLAY'], count: 28, companyId: 'hotjar' },
    { name: 'Adobe', categories: ['ANALYTICS'], count: 22, companyId: 'adobe' },
  ],
};

const MOCK_TIMELINE = {
  apsScore: 58,
  totals: { companies: 24, domains: 51, requests: 1843 },
  topCategories: [
    { category: 'ADVERTISING', label: 'Advertising', count: 612, color: '#EF4444', percent: 33 },
    { category: 'ANALYTICS', label: 'Analytics', count: 498, color: '#3B82F6', percent: 27 },
    { category: 'SOCIAL', label: 'Social Media', count: 331, color: '#8B5CF6', percent: 18 },
    { category: 'SESSION_REPLAY', label: 'Session Replay', count: 221, color: '#F59E0B', percent: 12 },
    { category: 'CDN', label: 'CDN / Hosting', count: 181, color: '#6B7280', percent: 10 },
  ],
  topCompanies: [
    { name: 'Google', count: 412, companyId: 'google' },
    { name: 'Meta', count: 287, companyId: 'meta' },
    { name: 'Amazon', count: 198, companyId: 'amazon' },
    { name: 'Hotjar', count: 156, companyId: 'hotjar' },
    { name: 'Adobe', count: 134, companyId: 'adobe' },
  ],
};

const MOCK_RULES = {
  shut: [
    { companyId: 'Meta', domains: ['connect.facebook.net', 'pixel.facebook.com', 'graph.facebook.com'], type: 'shut' },
    { companyId: 'Criteo', domains: ['static.criteo.net', 'bidder.criteo.com'], type: 'shut' },
  ],
  pause: [
    { companyId: 'Hotjar', domains: ['static.hotjar.com', 'script.hotjar.com'], type: 'pause', timeLeft: '1h 42m left' },
    { companyId: 'DoubleVerify', domains: ['cdn.doubleverify.com'], type: 'pause', timeLeft: '5h 18m left' },
  ],
};

const catColors = {
  ADVERTISING: '#EF4444',
  ANALYTICS: '#3B82F6',
  SOCIAL: '#8B5CF6',
  SESSION_REPLAY: '#F59E0B',
  CDN: '#6B7280',
};

function ExtFrame({ children, title, id }) {
  return (
    <div className="flex flex-col items-center" id={id}>
      <p className="text-sm font-medium text-slate-500 mb-3 uppercase tracking-wider">{title}</p>
      <div className="w-[400px] bg-slate-900 rounded-2xl overflow-hidden shadow-2xl shadow-black/40 border border-slate-700/50" style={{ minHeight: 540 }}>
        {children}
      </div>
    </div>
  );
}

function ExtHeader({ statusText = 'Monitoring', active = true }) {
  return (
    <header className="bg-slate-800 border-b border-slate-700 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-white text-sm">AgentCheck</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${active ? 'bg-green-500' : 'bg-slate-500'}`} />
          <span className="text-xs text-slate-400">{statusText}</span>
        </div>
      </div>
    </header>
  );
}

function ExtNav({ active = 'today' }) {
  const items = [
    { id: 'today', icon: Shield, label: 'Today' },
    { id: 'timeline', icon: BarChart2, label: 'Timeline' },
    { id: 'rules', icon: List, label: 'Rules' },
    { id: 'settings', icon: SettingsIcon, label: 'Settings' },
  ];
  return (
    <nav className="bg-slate-800 border-t border-slate-700 px-2 py-2">
      <div className="flex justify-around">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = active === item.id;
          return (
            <div key={item.id} className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg ${isActive ? 'text-blue-400 bg-blue-500/10' : 'text-slate-400'}`}>
              <Icon className="w-5 h-5" />
              <span className="text-xs">{item.label}</span>
            </div>
          );
        })}
      </div>
    </nav>
  );
}

// Screenshot 1: Onboarding
function OnboardingMock() {
  return (
    <ExtFrame title="1. Onboarding — Monitor OFF by default" id="ss-onboarding">
      <div className="p-4 flex flex-col" style={{ minHeight: 520 }}>
        <div className="text-center py-5">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/20">
            <Shield className="w-9 h-9 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">AgentCheck Monitor</h1>
          <p className="text-slate-400 text-sm mt-1">See what contacts you while browsing</p>
        </div>
        <div className="space-y-3 mb-5">
          <div className="flex gap-3 items-start">
            <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-200">Track third-party requests</p>
              <p className="text-xs text-slate-500">See which companies track you across the web</p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-200">Block unwanted trackers</p>
              <p className="text-xs text-slate-500">Shut or pause tracking from any company</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 mb-4">
          <h3 className="text-sm font-medium text-slate-300 mb-3">Monitoring Scope</h3>
          <div className="space-y-2">
            <div className="w-full p-3 rounded-lg border bg-blue-500/10 border-blue-500/30 text-left">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-blue-400">This site only</span>
                    <span className="text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">Recommended</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">Monitor only the current website</p>
                </div>
                <div className="w-5 h-5 rounded-full border-2 border-blue-500 bg-blue-500 flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              </div>
            </div>
            <div className="w-full p-3 rounded-lg border bg-slate-900/50 border-slate-700 text-left">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-slate-300">All sites</span>
                  <p className="text-xs text-slate-500 mt-0.5">Monitor all websites you visit</p>
                </div>
                <div className="w-5 h-5 rounded-full border-2 border-slate-600" />
              </div>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 mb-4">
          <div className="flex gap-2">
            <AlertCircle className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-400">AgentCheck stores data locally. No browsing history, URLs, or personal data is sent to any server.</p>
          </div>
        </div>
        <div className="w-full py-3 px-4 bg-blue-500 text-white font-medium rounded-xl flex items-center justify-center gap-2 text-sm">
          Enable Monitor
          <ChevronRight className="w-5 h-5" />
        </div>
        <div className="w-full py-2 text-slate-500 text-sm mt-2 text-center">Maybe later</div>
      </div>
    </ExtFrame>
  );
}

// Screenshot 2: Today View
function TodayMock() {
  const d = MOCK_TODAY;
  return (
    <ExtFrame title="2. Today View — Top companies" id="ss-today">
      <ExtHeader />
      <div className="p-4 space-y-3 flex-1 overflow-auto" style={{ maxHeight: 440 }}>
        {/* APS */}
        <div className="rounded-xl border p-4 bg-orange-500/20 border-orange-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs mb-1">Agent Pressure Score</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-orange-400">{d.apsScore}</span>
                <span className="text-slate-500 text-sm">/ 100</span>
              </div>
              <p className="text-xs mt-1 text-orange-400">High tracker activity</p>
            </div>
            <div className="w-14 h-14 rounded-full border-4 border-orange-400 flex items-center justify-center text-orange-400">
              <Shield className="w-7 h-7" />
            </div>
          </div>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { icon: Building2, label: 'Companies', value: d.totals.companies },
            { icon: Globe, label: 'Domains', value: d.totals.domains },
            { icon: Activity, label: 'Requests', value: d.totals.requests },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="bg-slate-800 rounded-xl p-2.5 text-center">
              <div className="text-slate-400 flex justify-center mb-0.5"><Icon className="w-4 h-4" /></div>
              <div className="text-lg font-bold text-white">{value}</div>
              <div className="text-xs text-slate-500">{label}</div>
            </div>
          ))}
        </div>
        {/* Categories */}
        <div className="bg-slate-800 rounded-xl p-3">
          <h3 className="text-xs font-medium text-slate-300 mb-2">Top Categories</h3>
          <div className="space-y-1.5">
            {d.topCategories.map((cat) => (
              <div key={cat.category} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
                <span className="text-xs text-slate-300 flex-1">{cat.label}</span>
                <span className="text-xs text-slate-500">{cat.count}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Companies */}
        <div className="bg-slate-800 rounded-xl p-3">
          <h3 className="text-xs font-medium text-slate-300 mb-2">Top Companies</h3>
          <div className="space-y-2">
            {d.topCompanies.map((company, i) => (
              <div key={company.companyId} className="flex items-center gap-2">
                <span className="text-slate-600 text-xs w-3">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-200">{company.name}</p>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: catColors[company.categories[0]] || '#6B7280' }} />
                    <span className="text-[10px] text-slate-500">{company.categories[0]?.replace('_', ' ')}</span>
                  </div>
                </div>
                <span className="text-xs text-slate-400 font-mono">{company.count}</span>
                <div className="flex gap-0.5">
                  <div className="p-1 text-amber-400"><Clock className="w-3.5 h-3.5" /></div>
                  <div className="p-1 text-red-400"><Ban className="w-3.5 h-3.5" /></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <ExtNav active="today" />
    </ExtFrame>
  );
}

// Screenshot 3: Timeline
function TimelineMock() {
  const d = MOCK_TIMELINE;
  return (
    <ExtFrame title="3. Timeline — 24h to YTD toggles" id="ss-timeline">
      <ExtHeader />
      <div className="p-4 space-y-3" style={{ maxHeight: 440, overflow: 'auto' }}>
        {/* Range selector */}
        <div className="flex bg-slate-800 rounded-lg p-1">
          {['24h', '48h', '30d', '90d', 'YTD'].map((r) => (
            <div key={r} className={`flex-1 py-2 text-xs font-medium rounded-md text-center ${r === '7d' ? '' : r === '30d' ? 'bg-blue-500 text-white' : 'text-slate-400'}`}
              style={r === '30d' ? { background: '#3B82F6', color: 'white' } : {}}>
              {r}
            </div>
          ))}
        </div>
        {/* Summary */}
        <div className="bg-slate-800 rounded-xl p-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-slate-400 text-xs">Agent Pressure Score</p>
              <p className="text-3xl font-bold text-amber-400">{d.apsScore}</p>
            </div>
            <div className="text-right">
              <p className="text-slate-400 text-xs">Last 30 days</p>
              <p className="text-base font-medium text-slate-300">{d.totals.requests.toLocaleString()} requests</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-700">
            <div>
              <p className="text-slate-500 text-[10px]">Companies</p>
              <p className="text-lg font-semibold text-white">{d.totals.companies}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Domains</p>
              <p className="text-lg font-semibold text-white">{d.totals.domains}</p>
            </div>
          </div>
        </div>
        {/* Category breakdown with bars */}
        <div className="bg-slate-800 rounded-xl p-3">
          <h3 className="text-xs font-medium text-slate-300 mb-2">Category Breakdown</h3>
          <div className="space-y-2">
            {d.topCategories.map((cat) => (
              <div key={cat.category}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-xs text-slate-300">{cat.label}</span>
                  <span className="text-xs text-slate-500">{cat.percent}%</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${cat.percent}%`, backgroundColor: cat.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Top companies */}
        <div className="bg-slate-800 rounded-xl p-3">
          <h3 className="text-xs font-medium text-slate-300 mb-2">Top Companies</h3>
          <div className="space-y-1.5">
            {d.topCompanies.map((company, i) => (
              <div key={company.companyId} className="flex items-center gap-2 py-0.5">
                <span className="text-slate-600 text-xs w-3">{i + 1}</span>
                <span className="flex-1 text-xs text-slate-300">{company.name}</span>
                <span className="text-xs text-slate-500 font-mono">{company.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <ExtNav active="timeline" />
    </ExtFrame>
  );
}

// Screenshot 4: Company Detail (Shut/Pause/Continue)
function CompanyDetailMock() {
  return (
    <ExtFrame title="4. Company Detail — Shut / Pause / Continue" id="ss-company">
      <ExtHeader />
      <div className="p-4 space-y-3" style={{ maxHeight: 440, overflow: 'auto' }}>
        {/* APS card mini */}
        <div className="rounded-xl border p-3 bg-orange-500/20 border-orange-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-[10px] mb-0.5">Agent Pressure Score</p>
              <span className="text-3xl font-bold text-orange-400">62</span>
              <span className="text-slate-500 text-sm ml-1">/ 100</span>
            </div>
            <div className="w-12 h-12 rounded-full border-4 border-orange-400 flex items-center justify-center text-orange-400">
              <Shield className="w-6 h-6" />
            </div>
          </div>
        </div>
        {/* Companies with action buttons highlighted */}
        <div className="bg-slate-800 rounded-xl p-3">
          <h3 className="text-xs font-medium text-slate-300 mb-3">Companies Detected Today</h3>
          <div className="space-y-2.5">
            {[
              { name: 'Google', cat: 'Advertising', count: 67, color: '#EF4444', status: null },
              { name: 'Meta', cat: 'Social Media', count: 43, color: '#8B5CF6', status: 'shut' },
              { name: 'Amazon', cat: 'Advertising', count: 31, color: '#EF4444', status: null },
              { name: 'Hotjar', cat: 'Session Replay', count: 28, color: '#F59E0B', status: 'paused' },
              { name: 'Adobe', cat: 'Analytics', count: 22, color: '#3B82F6', status: null },
              { name: 'Criteo', cat: 'Advertising', count: 18, color: '#EF4444', status: 'shut' },
            ].map((company, i) => (
              <div key={company.name} className="flex items-center gap-2">
                <span className="text-slate-600 text-xs w-3">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-200">{company.name}</p>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: company.color }} />
                    <span className="text-[10px] text-slate-500">{company.cat}</span>
                  </div>
                </div>
                <span className="text-xs text-slate-400 font-mono mr-1">{company.count}</span>
                {company.status === 'shut' ? (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/15 border border-red-500/20">
                    <Ban className="w-3 h-3 text-red-400" />
                    <span className="text-[10px] text-red-400 font-medium">Shut</span>
                    <Play className="w-3 h-3 text-green-400 ml-1" />
                  </div>
                ) : company.status === 'paused' ? (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/15 border border-amber-500/20">
                    <Clock className="w-3 h-3 text-amber-400" />
                    <span className="text-[10px] text-amber-400 font-medium">1h 42m</span>
                    <Play className="w-3 h-3 text-green-400 ml-1" />
                  </div>
                ) : (
                  <div className="flex gap-1">
                    <div className="p-1.5 text-amber-400 hover:bg-amber-500/10 rounded"><Clock className="w-3.5 h-3.5" /></div>
                    <div className="p-1.5 text-red-400 hover:bg-red-500/10 rounded"><Ban className="w-3.5 h-3.5" /></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        {/* Legend */}
        <div className="bg-slate-800/50 rounded-lg p-3">
          <div className="flex items-center gap-4 justify-center">
            <div className="flex items-center gap-1.5">
              <Ban className="w-3 h-3 text-red-400" />
              <span className="text-[10px] text-slate-400">Shut (permanent block)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-amber-400" />
              <span className="text-[10px] text-slate-400">Pause (timed block)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Play className="w-3 h-3 text-green-400" />
              <span className="text-[10px] text-slate-400">Continue (unblock)</span>
            </div>
          </div>
        </div>
      </div>
      <ExtNav active="today" />
    </ExtFrame>
  );
}

// Screenshot 5: Rules with expiry
function RulesMock() {
  return (
    <ExtFrame title="5. Rules — Active blocks with expiry timers" id="ss-rules">
      <ExtHeader />
      <div className="p-4 space-y-3" style={{ maxHeight: 440, overflow: 'auto' }}>
        {/* Shut rules */}
        <div className="bg-slate-800 rounded-xl p-3">
          <h3 className="text-xs font-medium text-red-400 mb-2 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" />
            Blocked (2)
          </h3>
          <div className="space-y-1">
            {MOCK_RULES.shut.map((rule) => (
              <div key={rule.companyId} className="border-b border-slate-700 last:border-0 pb-2 last:pb-0">
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2 flex-1">
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                    <div>
                      <p className="text-xs font-medium text-slate-200">{rule.companyId}</p>
                      <p className="text-[10px] text-slate-500">{rule.domains.length} domains</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 text-slate-400 hover:text-green-400 rounded-lg">
                    <Play className="w-3.5 h-3.5 text-green-400" />
                    <span className="text-[10px] text-green-400">Unblock</span>
                  </div>
                </div>
                <div className="ml-6 space-y-0.5 mb-1">
                  {rule.domains.map((domain) => (
                    <div key={domain} className="flex items-center gap-1.5 text-[10px] text-slate-500">
                      <Globe className="w-2.5 h-2.5" />
                      <span>{domain}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Pause rules */}
        <div className="bg-slate-800 rounded-xl p-3">
          <h3 className="text-xs font-medium text-amber-400 mb-2 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            Paused (2)
          </h3>
          <div className="space-y-1">
            {MOCK_RULES.pause.map((rule) => (
              <div key={rule.companyId} className="border-b border-slate-700 last:border-0 pb-2 last:pb-0">
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2 flex-1">
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                    <div>
                      <p className="text-xs font-medium text-slate-200">{rule.companyId}</p>
                      <p className="text-[10px] text-slate-500">
                        {rule.domains.length} domain{rule.domains.length > 1 ? 's' : ''}
                        <span className="ml-1.5 text-amber-400">{rule.timeLeft}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 rounded-lg">
                    <Play className="w-3.5 h-3.5 text-green-400" />
                    <span className="text-[10px] text-green-400">Unblock</span>
                  </div>
                </div>
                <div className="ml-6 space-y-0.5 mb-1">
                  {rule.domains.map((domain) => (
                    <div key={domain} className="flex items-center gap-1.5 text-[10px] text-slate-500">
                      <Globe className="w-2.5 h-2.5" />
                      <span>{domain}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="text-[10px] text-slate-500 text-center">Total blocked domains: 8</p>
      </div>
      <ExtNav active="rules" />
    </ExtFrame>
  );
}

// Screenshot 6: Settings
function SettingsMock() {
  return (
    <ExtFrame title="6. Settings — Export & Delete controls" id="ss-settings">
      <ExtHeader />
      <div className="p-4 space-y-3" style={{ maxHeight: 440, overflow: 'auto' }}>
        {/* Monitor Status */}
        <div className="bg-slate-800 rounded-xl p-3">
          <h3 className="text-xs font-medium text-slate-300 mb-2 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 text-blue-400" />
            Monitor Status
          </h3>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Monitoring</span>
              <span className="text-xs font-medium text-green-400">Active</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Scope</span>
              <span className="text-xs text-slate-300 flex items-center gap-1">
                <Globe className="w-3 h-3" /> All sites
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Blocking</span>
              <span className="text-xs font-medium text-green-400">Active</span>
            </div>
            <div className="w-full mt-2 py-2 px-3 bg-slate-700 text-slate-300 text-xs rounded-lg text-center">
              Disable Monitor
            </div>
          </div>
        </div>
        {/* Data Management */}
        <div className="bg-slate-800 rounded-xl p-3">
          <h3 className="text-xs font-medium text-slate-300 mb-2">Data Management</h3>
          <div className="space-y-2">
            <div className="w-full py-2.5 px-3 bg-blue-500/10 text-blue-400 text-xs rounded-lg flex items-center justify-center gap-2">
              <Download className="w-3.5 h-3.5" />
              Export Data as JSON
            </div>
            <div className="w-full py-2.5 px-3 bg-red-500/10 text-red-400 text-xs rounded-lg flex items-center justify-center gap-2">
              <Trash2 className="w-3.5 h-3.5" />
              Delete All Data
            </div>
          </div>
        </div>
        {/* Privacy */}
        <div className="bg-slate-800/50 rounded-xl p-3">
          <h3 className="text-xs font-medium text-slate-300 mb-2">Privacy</h3>
          <ul className="space-y-1">
            {[
              'All data stored locally on your device',
              'No URLs or page content stored',
              'Only domain-level data collected',
              'Data auto-expires after 7 days',
            ].map((text) => (
              <li key={text} className="text-[10px] text-slate-500 flex items-start gap-1.5">
                <span className="text-green-400 mt-0.5 text-xs">&#10003;</span>
                {text}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-[10px] text-slate-600 text-center">AgentCheck Monitor v1.0.0</p>
      </div>
      <ExtNav active="settings" />
    </ExtFrame>
  );
}

export default function CWSScreenshots() {
  return (
    <div className="min-h-screen bg-slate-950 py-12 px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-2xl font-bold text-white mb-2">Chrome Web Store Screenshots</h1>
          <p className="text-slate-400 text-sm">AgentCheck Monitor — Store listing mockups (synthetic data, no personal info)</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          <OnboardingMock />
          <TodayMock />
          <TimelineMock />
          <CompanyDetailMock />
          <RulesMock />
          <SettingsMock />
        </div>
      </div>
    </div>
  );
}
